import { useState, useEffect } from "react";
import { CheckCompany, isCheckError, CheckResponseError, CheckSignatory } from "winslow-core";
import { Alert, AlertDescription } from "../../../ds2/components";
import { CheckEmbeddedComponentV2 } from "../../payroll/CheckEmbeddedComponentV2";
import { apiCheckGetCompanySignatoryAgreementsComponent } from "../../../services/PayrollService";
import toast from "react-hot-toast";
import { toastFail } from "../../../config";

type AppPayBankAccountAuthorizationProps = {
    checkCompanyId: string | null | undefined;
    checkSignatory: CheckSignatory | null | undefined;
    onComplete?: () => void;
    onError?: (error: CheckResponseError | undefined) => void;
};

export const AppPayBankAccountAuthorization = ({
    checkCompanyId,
    checkSignatory,
    onComplete,
    onError,
}: AppPayBankAccountAuthorizationProps) => {
    const [componentUrl, setComponentUrl] = useState<string | null>(null);
    const [loadingComponent, setLoadingComponent] = useState<boolean>(false);
    const [checkResponseError, setCheckResponseError] = useState<CheckResponseError | undefined>(undefined);

    // Load component URL when component mounts or checkCompanyId/checkSignatory changes
    useEffect(() => {
        if (checkCompanyId && checkSignatory?.id) {
            loadComponentUrl();
        } else {
            setComponentUrl(null);
            setCheckResponseError(undefined);
        }
    }, [checkCompanyId, checkSignatory]);

    const loadComponentUrl = async () => {
        if (!checkCompanyId || !checkSignatory?.id) {
            return;
        }

        setLoadingComponent(true);
        setCheckResponseError(undefined);

        try {
            const apiResponse = await apiCheckGetCompanySignatoryAgreementsComponent(checkCompanyId, checkSignatory.id);
            const url = apiResponse?.data;
            setCheckResponseError(apiResponse?.checkResponseError);

            if (apiResponse?.checkResponseError && onError) {
                onError(apiResponse.checkResponseError);
            }

            if (!apiResponse || isCheckError(apiResponse) || !url) {
                toast("Error loading signatory agreements component", toastFail);
                return;
            }

            setComponentUrl(url);
        } catch (error) {
            console.error("[AppPayBankAccountAuthorization] Error loading component URL:", error);
            toast("Error loading signatory agreements component", toastFail);
        } finally {
            setLoadingComponent(false);
        }
    };

    return (
        <div className="app-pay-enroll-new-contents">
            <div className="app-pay-enroll-new-left">
                {checkResponseError && (
                    <Alert variant="warning" style={{ marginBottom: "16px" }}>
                        <AlertDescription>
                            {checkResponseError.errorMessage || "An error occurred"}
                        </AlertDescription>
                    </Alert>
                )}

                {!checkSignatory || !checkSignatory.id ? (
                    <Alert variant="warning">
                        <AlertDescription>
                            No valid signatory
                        </AlertDescription>
                    </Alert>
                ) : loadingComponent ? (
                    <div style={{ padding: "40px", textAlign: "center" }}>
                        Loading signatory agreements component...
                    </div>
                ) : componentUrl ? (
                    <div className="app-pay-enroll-embed">
                        <CheckEmbeddedComponentV2
                            url={componentUrl}
                            title="Bank Account Authorization"
                            onEvent={(event, data) => {
                                console.log("[AppPayBankAccountAuthorization] Check component event:", event, data);
                                // Handle completion event if needed
                                if (event === "check-component-company-signatory-agreements-already-complete" || 
                                    event === "check-component-company-signatory-agreements-complete") {
                                    if (onComplete) {
                                        onComplete();
                                    }
                                }
                            }}
                        />
                    </div>
                ) : (
                    <Alert variant="warning">
                        <AlertDescription>
                            Component not found.
                        </AlertDescription>
                    </Alert>
                )}
            </div>
        </div>
    );
};

